import { runInAction } from "mobx";
import { fetch } from "./api-wrapper";
import DataStore from "./data-store";
import { mongoExtendedJsonId } from "../pages/review-expense/utils/contract-expense-json";
import { languageFromPreference } from "../locales/translator";

const normalize = (str) => (str ? str.toLowerCase().replace(/[\s.]/g, "") : "");

const HEX_OBJECT_ID = /^[0-9a-fA-F]{24}$/;

export function isXpensCategoryHexId(value) {
  const unwrapped = mongoExtendedJsonId(value);
  return Boolean(unwrapped && HEX_OBJECT_ID.test(unwrapped));
}

// Keyed by the data store instance, not a single shared variable — there is
// only one data store per real app session, but keying it per-instance keeps
// two independent stores (e.g. two test fixtures) from sharing an in-flight
// request that belongs to a different store entirely (XPEN-3187 PR review).
const inFlightRequests = new WeakMap();

/**
 * Fetches the standard XPENS category list once per session and caches it
 * on the shared data store (data.xpensCategories). A no-op if already
 * cached — callers can call this on every mount without triggering a
 * refetch (XPEN-3187). Concurrent calls for the same data store while a
 * fetch is already in flight share that same request rather than firing
 * a duplicate.
 *
 * @param {Object} data - The MobX observable data store
 * @param {Function} [onError] - Called with a translated message on failure
 */
export function loadXpensCategories(data, onError) {
  if (data.xpensCategories) return;

  let inFlightRequest = inFlightRequests.get(data);
  if (!inFlightRequest) {
    inFlightRequest = new Promise((resolve) => {
      fetch(
        "/xpens-categories/",
        "GET",
        {},
        (response) => {
          runInAction(() => {
            DataStore.setXpensCategories(data, Array.isArray(response.data) ? response.data : []);
          });
          resolve(true);
        },
        (err) => {
          // Log only the message, never the raw error object — axios attaches the
          // request config (including the Authorization header) to thrown errors.
          console.error("Failed to load XPENS categories:", err?.message ?? "unknown error");
          resolve(false);
        }
      );
    }).finally(() => {
      inFlightRequests.delete(data);
    });
    inFlightRequests.set(data, inFlightRequest);
  }

  if (onError) {
    inFlightRequest.then((succeeded) => {
      if (!succeeded) onError(data.t.read("error_xpens_categories_load_failed"));
    });
  }
}

/**
 * Match a stored workflow / linkage value to a XPENS category row.
 * Hex ids (plain or `{ $oid }`) win first so leftover names are never
 * treated as missing ids. Name / code match remains for unmigrated rows.
 */
export function findMatchingCategory(value, categories) {
  if (!Array.isArray(categories) || categories.length === 0 || value == null || value === "") {
    return undefined;
  }
  const unwrapped = mongoExtendedJsonId(value);
  if (!unwrapped) return undefined;
  if (isXpensCategoryHexId(unwrapped)) {
    const needle = unwrapped.toLowerCase();
    return categories.find(
      (c) => (mongoExtendedJsonId(c._id) || "").toLowerCase() === needle
    );
  }
  const target = normalize(unwrapped);
  return categories.find(
    (c) => normalize(c.name_en) === target || normalize(c.code) === target || normalize(c.name_fr) === target
  );
}

/**
 * Language used for XPENS category names: app toggle first, then the firm's
 * language preference, then English. Translator already normalizes unknown
 * values to `en` or `fr`, so the firm fallback is for an unset `t.lang`.
 *
 * @param {Object} [data] - The MobX observable data store
 * @returns {"en" | "fr"}
 */
export function resolveCategoryDisplayLang(data) {
  const lang = data?.t?.lang;
  if (lang === "fr" || lang === "en") return lang;
  return languageFromPreference(data?.config?.language_preference) === "fr" ? "fr" : "en";
}

/**
 * Resolves a stored category value to its display label in the user's selected
 * language, from the cached standard XPENS category list. Falls back to
 * the leftover name (then n_a) when the cache is empty or the value has no match.
 * Unresolved hex ids show n_a, never the raw ObjectId (XPEN-3183-006 / XPEN-3328).
 *
 * @param {Object} data - The MobX observable data store
 * @param {string} category - Hex id, `{ $oid }`, name, or code
 * @returns {string}
 */
export function getXpensCategoryLabel(data, category) {
  const match = findMatchingCategory(category, data.xpensCategories);
  if (match) {
    return resolveCategoryDisplayLang(data) === "fr" ? match.name_fr : match.name_en;
  }
  return (isXpensCategoryHexId(category) ? null : mongoExtendedJsonId(category)) || data.t.read("n_a");
}

/**
 * Whether a value names one of the currently active standard XPENS
 * categories — written as its stable `code`, its `name_en`, or its
 * `name_fr`. All three occur on live data: the linkage picker has written
 * the display `name_en` since XPEN-3251 — before that (XPEN-3187 through
 * XPEN-3189's PR review) it wrote the code, a format that never matched the
 * backend's exact-match lookup, so every hand-made link silently did
 * nothing until XPEN-3251 fixed the picker and repaired the already-saved
 * rows. patch_xpen_3189_xpens_categories_v2.py writes the full name_en when
 * it repairs an existing link during a migration — a check that only
 * recognized the code format flagged every migration-repaired link as
 * stale, XPENS's own default categories included.
 *
 * @param {string | null | undefined} value
 * @param {Array<{code: string, name_en: string, name_fr: string}> | null | undefined} categories
 * @returns {boolean}
 */
export function isKnownXpensCategory(value, categories) {
  return findMatchingCategory(value, categories) !== undefined;
}
